"use client";
import React from "react";
import { TradeCard } from "modules/swap-calculator/model/tradeCard";
import { FinishEnum } from "modules/cards/model/card";
import { useSets } from "modules/common/hooks/useSets";
import { SetIcon } from "modules/common/components/SetIcon";
import { cn } from "utils/utils";
import { formatCurrency } from "utils/currency";
import { useSwapModal } from "modules/swap-calculator/stores/useSwapCalculatorStore";
import { finishSpec } from "modules/cards/model/finishBehavior";

type Props = {
  card: TradeCard;
  isRemoving?: boolean;
  onRemoveComplete?: () => void;
};

function TradeListItem({ card, isRemoving = false, onRemoveComplete }: Props) {
  const isFoil = finishSpec(card.finish).foilVisual;
  // Optimal Zustand selector - only re-renders when modal state changes
  const { openModal } = useSwapModal();
  const { getSetName } = useSets();
  const setName = getSetName(card.setCode, card.game);

  const showEditModal = () => {
    openModal(card);
  };

  return (
    <div
      className={cn(
        "card bg-base-100 cursor-pointer border transition-all hover:scale-[1.01]",
        // Animation classes
        isRemoving
          ? "animate-out slide-out-to-bottom"
          : "animate-in slide-in-from-bottom",
        // Color classes
        card.list === "left"
          ? "border-secondary hover:bg-secondary/10"
          : "border-primary hover:bg-primary/10"
      )}
      onClick={showEditModal}
      onAnimationEnd={() => {
        if (isRemoving && onRemoveComplete) {
          onRemoveComplete();
        }
      }}
    >
      <div className="card-body p-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-12">
          {/* Card Name */}
          <div className="sm:col-span-7">
            <h3
              className={cn(
                "text-base font-medium md:text-xl",
                card.list === "left" ? "text-secondary" : "text-primary"
              )}
            >
              {card.name}
            </h3>
          </div>

          {/* Set Info - Hidden on Mobile */}
          <div className="hidden sm:col-span-5 sm:flex sm:justify-end">
            <div
              className={cn(
                "tooltip",
                card.list === "left" ? "tooltip-secondary" : "tooltip-primary"
              )}
              data-tip={setName}
            >
              <span
                className={cn(
                  "flex items-center gap-1 text-sm md:text-lg",
                  card.list === "left" ? "text-secondary" : "text-primary"
                )}
              >
                <SetIcon
                  setCode={card.setCode}
                  game={card.game}
                  className="ss-fw sm:ss-2x"
                />
                <span>{` ${card.setCode} (${card.number})`}</span>
              </span>
            </div>
          </div>

          {/* Price and Quantity */}
          <div className="flex items-center sm:col-span-6">
            <span
              className={cn(
                "text-sm md:text-lg",
                card.list === "left" ? "text-secondary" : "text-primary"
              )}
            >
              {`${formatCurrency(card.customPrice ?? 0, card.price.currency)} x ${card.quantity}`}
            </span>
          </div>

          {/* Foil Badge */}
          <div className="flex items-center justify-end sm:col-span-6">
            {isFoil && (
              <div
                className={cn(
                  "badge badge-soft md:badge-lg text-sm md:text-base",
                  card.list === "left" ? "badge-secondary" : "badge-primary"
                )}
              >
                Foil
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TradeListItem;
